import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Package, MapPin, Plus, Trash2, Crosshair, Save, Layers, Loader2 } from "lucide-react";
import { MultiImageUploader } from "@/components/MultiImageUploader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (path) => `${BACKEND}/api/files/${path}`;
const CATEGORIES = ["motor", "propeller", "battery", "esc", "frame", "camera", "misc"];
const emptyPart = { name: "", category: "motor", sku: "", price: 0, stock: 0, description: "", compatible_with: "", image_path: null, images: [] };

function flattenTree(items, partMap, depth, pathKey, acc) {
  for (const s of items) {
    const p = partMap[s.part_id];
    if (!p) continue;
    const key = `${pathKey}/${s.part_id}`;
    acc.push({ key, partId: s.part_id, qty: s.qty, depth });
    const kids = p.sub_parts || [];
    if (kids.length && depth < 8 && !pathKey.includes(s.part_id)) flattenTree(kids, partMap, depth + 1, key, acc);
  }
  return acc;
}

export default function PartEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(true);
  const [allParts, setAllParts] = useState([]);
  const [form, setForm] = useState(emptyPart);
  const [subParts, setSubParts] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [addId, setAddId] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get("/parts");
    setAllParts(r.data);
    if (!isNew) {
      const p = r.data.find((x) => x.id === id);
      if (p) { setForm({ ...emptyPart, ...p }); setSubParts(p.sub_parts || []); setHotspots(p.hotspots || []); }
      else toast.error("Part not found");
    }
    setLoading(false);
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const partMap = useMemo(() => Object.fromEntries(allParts.map((p) => [p.id, p])), [allParts]);
  const candidates = allParts.filter((p) => p.id !== id && !subParts.some((s) => s.part_id === p.id));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImg = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("image_path", r.data.path); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const addSub = () => {
    if (!addId) { toast.error("Pick a part to add"); return; }
    setSubParts((s) => [...s, { part_id: addId, qty: +addQty || 1 }]);
    setAddId(""); setAddQty(1);
  };
  const removeSub = (pid) => {
    setSubParts((s) => s.filter((x) => x.part_id !== pid));
    setHotspots((h) => h.filter((x) => x.part_id !== pid));
    if (selectedId === pid) setSelectedId(null);
  };
  const setQty = (pid, qty) => setSubParts((s) => s.map((x) => x.part_id === pid ? { ...x, qty: +qty || 1 } : x));

  const onImageClick = (e) => {
    if (!placing) return;
    if (!selectedId) { toast.error("Select a sub-part first, then place its pin"); return; }
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setHotspots((h) => [...h.filter((p) => p.part_id !== selectedId), { id: `hs_${Date.now()}`, x, y, part_id: selectedId, label: "" }]);
    setPlacing(false);
    toast.success("Pin placed");
  };
  const pinNumber = (pid) => { const i = hotspots.findIndex((h) => h.part_id === pid); return i >= 0 ? i + 1 : null; };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = { ...form, price: +form.price || 0, stock: +form.stock || 0, sub_parts: subParts, hotspots: hotspots.map((h, i) => ({ ...h, label: String(i + 1) })) };
    delete payload.id; delete payload.created_at;
    try {
      let pid = id;
      if (isNew) { const r = await api.post("/parts", payload); pid = r.data.id; }
      else await api.put(`/parts/${id}`, payload);
      toast.success("Part saved");
      navigate(pid ? `/parts/${pid}` : "/parts");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-6 fade-up" data-testid="part-edit-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-1" data-testid="part-edit-back"><ArrowLeft className="h-4 w-4" /> Back</button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-heading">{isNew ? "New Part" : `Edit — ${form.name}`}</h1>
        </div>
        <Button data-testid="save-part-btn" onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Part"}</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Part details */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 lg:col-span-1">
          <h2 className="font-heading font-semibold text-slate-900">Details</h2>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label><Input data-testid="part-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">SKU</Label><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Price (AUD)</Label><Input type="number" data-testid="part-price-input" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Stock</Label><Input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Compatible With</Label><Input value={form.compatible_with} onChange={(e) => set("compatible_with", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description / Diagram notes</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" rows={3} /></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Diagram / Main photo</Label>
            <Input data-testid="part-image-input" type="file" accept="image/*" onChange={(e) => uploadImg(e.target.files[0])} className="mt-1.5" />
            {uploading && <p className="text-xs text-slate-400 mt-1">Uploading…</p>}
            {form.image_path && <p className="text-xs text-lime-600 mt-1">Diagram image attached ✓</p>}</div>
          <MultiImageUploader value={form.images || []} onChange={(imgs) => set("images", imgs)} label="Additional photos" testid="part-images" />
        </div>

        {/* Sub-parts & diagram */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-4"><Layers className="h-5 w-5 text-lime-500" /> Sub-parts & diagram</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {/* Diagram */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Diagram</Label>
                <Button size="sm" variant={placing ? "default" : "outline"} data-testid="place-pin-btn" onClick={() => setPlacing((p) => !p)} className={placing ? "bg-lime-400 text-slate-900 hover:bg-lime-300" : ""} disabled={!form.image_path}>
                  <Crosshair className="h-3.5 w-3.5 mr-1" /> {placing ? "Click diagram…" : "Place Pin"}
                </Button>
              </div>
              <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-100 inline-block max-w-full align-top" data-testid="assembly-image-wrap">
                {form.image_path ? (
                  <img ref={imgRef} src={imgUrl(form.image_path)} alt={form.name} onClick={onImageClick} className={`block max-h-[360px] max-w-full w-auto ${placing ? "cursor-crosshair" : ""}`} />
                ) : (
                  <div className="h-64 w-full flex items-center justify-center text-sm text-slate-400 px-6 text-center">Upload a diagram photo (left) to place pins.</div>
                )}
                {hotspots.map((h, i) => (
                  <button key={h.id} data-testid={`hotspot-${h.part_id}`} onClick={() => setSelectedId(h.part_id)} style={{ left: `${h.x}%`, top: `${h.y}%` }}
                    className={`absolute -translate-x-1/2 -translate-y-full flex flex-col items-center transition-transform ${selectedId === h.part_id ? "scale-125 z-10" : ""}`}>
                    <span className={`h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center shadow-md border-2 border-white ${selectedId === h.part_id ? "bg-lime-400 text-slate-900" : "bg-slate-900 text-white"}`}>{i + 1}</span>
                    <MapPin className={`h-3 w-3 -mt-1 ${selectedId === h.part_id ? "text-lime-400" : "text-slate-900"}`} fill="currentColor" />
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Select a sub-part, hit "Place Pin", then click the diagram.</p>
            </div>

            {/* Sub-parts list */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sub-parts ({subParts.length})</Label>
              <div className="mt-2 border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[300px] overflow-y-auto" data-testid="subparts-list">
                {subParts.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No sub-parts yet.</p>
                  : subParts.map((s) => {
                    const p = partMap[s.part_id]; const num = pinNumber(s.part_id); const active = selectedId === s.part_id;
                    return (
                      <div key={s.part_id} data-testid={`subpart-item-${s.part_id}`} className={`flex items-center gap-2 px-2 py-2 cursor-pointer ${active ? "bg-lime-50" : "hover:bg-slate-50"}`} onClick={() => setSelectedId(s.part_id)}>
                        {num ? <span className="h-5 w-5 shrink-0 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center">{num}</span> : <span className="h-5 w-5 shrink-0 rounded-full border border-dashed border-slate-300" title="No pin" />}
                        <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                          {p?.image_path ? <img src={imgUrl(p.image_path)} alt="" className="h-full w-full object-cover" /> : <Package className="h-3.5 w-3.5 text-slate-300" />}
                        </div>
                        <span className="text-sm text-slate-800 truncate flex-1">{p?.name || "Unknown part"}</span>
                        <Input type="number" min="1" value={s.qty} onClick={(e) => e.stopPropagation()} onChange={(e) => setQty(s.part_id, e.target.value)} className="h-7 w-14 text-xs px-1.5" data-testid={`subpart-qty-${s.part_id}`} />
                        {p?.sub_parts?.length > 0 && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-900" title="Edit its assembly" onClick={(e) => { e.stopPropagation(); navigate(`/parts/${p.id}/edit`); }} data-testid={`open-subassembly-${s.part_id}`}><Layers className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeSub(s.part_id); }} data-testid={`remove-subpart-${s.part_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    );
                  })}
              </div>
              <div className="mt-3 flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Add sub-part</Label>
                  <Select value={addId} onValueChange={setAddId}>
                    <SelectTrigger className="mt-1 h-9" data-testid="add-subpart-select"><SelectValue placeholder="Pick a part…" /></SelectTrigger>
                    <SelectContent>{candidates.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input type="number" min="1" value={addQty} onChange={(e) => setAddQty(e.target.value)} className="h-9 w-16" data-testid="add-subpart-qty" />
                <Button size="sm" onClick={addSub} className="h-9 bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-subpart-btn"><Plus className="h-4 w-4" /></Button>
              </div>

              {subParts.length > 0 && (
                <div className="mt-4">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Full tree</Label>
                  <div className="mt-1 border border-slate-100 rounded-lg py-1" data-testid="assembly-tree">
                    {flattenTree(subParts, partMap, 0, "", []).map((row) => {
                      const p = partMap[row.partId]; if (!p) return null; const active = selectedId === row.partId;
                      return (
                        <div key={row.key} data-testid={`tree-row-${row.partId}`} onClick={() => setSelectedId(row.partId)} className={`flex items-center gap-2 py-1.5 pr-2 rounded-lg cursor-pointer transition-colors ${active ? "bg-lime-50 ring-1 ring-lime-300" : "hover:bg-slate-50"}`} style={{ paddingLeft: `${8 + row.depth * 18}px` }}>
                          <div className="h-6 w-6 rounded bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                            {p.image_path ? <img src={imgUrl(p.image_path)} alt="" className="h-full w-full object-cover" /> : <Package className="h-3 w-3 text-slate-300" />}
                          </div>
                          <span className="text-sm text-slate-800 truncate flex-1">{p.name}</span>
                          {row.qty > 1 && <Badge variant="outline" className="text-[10px] border-slate-300">×{row.qty}</Badge>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
